import { ApolloError } from '@apollo/client'
import { Features } from 'generated/featureDefinitions'
import {
  ContractsEmployeesJobCatalogTreeFragment,
  GetTaskDetailsDocument,
  GetTaskDetailsQueryVariables,
  TaskDetailAffecteeReferenceContractFragment,
  TaskDetailContractContextlessPoliciesFragment,
  TaskDetailCustomFieldsContextlessPoliciesFragment,
  TaskDetailFragment,
  TaskDetailJobCatalogContextlessPoliciesFragment,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import { TaskAssignee } from 'modules/tasks/components/TasksList/types'

export interface TaskDetail extends TaskDetailFragment {
  assignees: TaskAssignee[]
  assigneesCount: number
}

interface UseTaskDetails {
  task?: TaskDetail
  loading: boolean
  error: ApolloError | undefined
  canSeePersonalInfo: boolean
  canEditPersonalInfo: boolean
  canEmployeeManageAnyEmployeeFolder: boolean
  hasDevTimeTrackingWorkableDaysDefinition: boolean
  hasTimeTrackingAnnualisationDailyValue: boolean
  hasPayrollBundle: boolean
  jobCatalogContextlessPolicies: TaskDetailJobCatalogContextlessPoliciesFragment | undefined
  contractContextlessPolicies: TaskDetailContractContextlessPoliciesFragment | undefined
  referenceContract: TaskDetailAffecteeReferenceContractFragment | undefined
  customFieldsContextlessPolicies: TaskDetailCustomFieldsContextlessPoliciesFragment | undefined
  jobCatalogTreeFeatureEnabled: boolean
  jobCatalogTrees: ContractsEmployeesJobCatalogTreeFragment[] | undefined
}

export const useTaskDetails = (variables: GetTaskDetailsQueryVariables): UseTaskDetails => {
  const { data, loading, error } = useQuery(GetTaskDetailsDocument, { variables })

  const task = data?.tasks.tasks[0]

  if (!task)
    return {
      task: undefined,
      loading: loading,
      error: error,
      canSeePersonalInfo: false,
      canEditPersonalInfo: false,
      canEmployeeManageAnyEmployeeFolder: false,
      hasDevTimeTrackingWorkableDaysDefinition: false,
      hasTimeTrackingAnnualisationDailyValue: false,
      hasPayrollBundle: false,
      jobCatalogContextlessPolicies: undefined,
      contractContextlessPolicies: undefined,
      referenceContract: undefined,
      customFieldsContextlessPolicies: undefined,
      jobCatalogTreeFeatureEnabled: false,
      jobCatalogTrees: undefined,
    }

  const assignees = task?.assigneesConnection.nodes || []
  const assigneesCount = task?.assigneesConnection.totalCount || 0
  const affecteeEmployee = task?.affecteesConnection.edges[0]?.node.employee

  const canSeePersonalInfo = affecteeEmployee?._policies?.readPersonalInfo.can ?? false
  const canEditPersonalInfo = affecteeEmployee?._policies?.editPersonalInfo.can ?? false
  const canEmployeeManageAnyEmployeeFolder =
    data?.documents._policies.employeeManageAnyEmployeeFolder.can ?? false

  const referenceContract = affecteeEmployee?.referenceContract ?? undefined
  const contractContextlessPolicies = data?.contracts._policies
  const jobCatalogContextlessPolicies = data?.jobCatalog?._policies
  const customFieldsContextlessPolicies = data?.customFields._policies
  const hasDevTimeTrackingWorkableDaysDefinition =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_time_tracking_workable_days_definition'
    )?.enabled ?? false
  const hasTimeTrackingAnnualisationDailyValue =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === Features.time_tracking_annualisation_daily_value
    )?.available ?? false
  const hasPayrollBundle =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === Features.payroll_management
    )?.available ?? false

  const hasLegacyJobCatalog =
    data?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'legacy_job_catalog'
    )?.enabled ?? false
  const jobCatalogTreeFeatureEnabled = !hasLegacyJobCatalog
  const jobCatalogTrees = data?.jobCatalog?.treesConnection?.nodes

  return {
    task: { ...task, assignees, assigneesCount },
    loading: loading,
    error: error,
    canSeePersonalInfo,
    canEditPersonalInfo,
    canEmployeeManageAnyEmployeeFolder,
    hasDevTimeTrackingWorkableDaysDefinition,
    hasTimeTrackingAnnualisationDailyValue,
    hasPayrollBundle,
    jobCatalogContextlessPolicies,
    contractContextlessPolicies,
    referenceContract,
    customFieldsContextlessPolicies,
    jobCatalogTreeFeatureEnabled,
    jobCatalogTrees,
  }
}
